import { useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";

import type { WidgetAbout } from "./theme";

interface WidgetChromeProps {
  open: boolean;
  onOpen: () => void;
  /**
   * Hover / focus / press on the collapsed launcher — a strong signal
   * the visitor is about to open, used to prefetch the panel chunk.
   */
  onLauncherIntent?: () => void;
  onClose: () => void;
  streaming: boolean;
  onSend: (text: string) => void;
  title: string;
  /** Optional one-line subtitle under the header title (brand tagline). */
  tagline?: string;
  /** When true, a small "Beta" badge renders next to the header title. */
  beta?: boolean;
  /** Short text shown on the collapsed launcher pill (e.g. the bot name). */
  launcherLabel: string;
  logoUrl: string | null;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  /**
   * ``mode=app`` embed (native-app webview): the panel is the whole
   * screen, so the close and fullscreen-toggle controls are hidden.
   */
  appMode: boolean;
  /** True when the host page (not the iframe!) is mobile-sized. */
  hostIsMobile: boolean;
  authSlot: ReactNode | null;
  about: WidgetAbout | null;
  children: ReactNode;
}

/**
 * Widget shell: a fixed-position launcher bubble that expands into
 * a panel with a header (title + close), a body slot (the
 * transcript), and a composer footer (textarea + send).
 *
 * The wrapper iframe is what's actually fixed-positioned on the
 * customer page; inside the iframe we still draw the launcher /
 * panel as fixed elements because the iframe is sized to the chrome
 * (and the loader resizes it on open/close).
 */
export function WidgetChrome(props: WidgetChromeProps) {
  const {
    open,
    onOpen,
    onLauncherIntent,
    onClose,
    streaming,
    onSend,
    title,
    tagline,
    beta,
    launcherLabel,
    logoUrl,
    fullscreen,
    onToggleFullscreen,
    appMode,
    hostIsMobile,
    authSlot,
    about,
    children,
  } = props;
  const [draft, setDraft] = useState("");
  const [aboutOpen, setAboutOpen] = useState(false);

  // Only offer the (i) button when the admin has actually populated
  // some about content. Empty defaults (title="" body="" links=[])
  // should not surface a useless modal.
  const aboutBody = (about?.body ?? "").trim();
  const aboutTitle = (about?.title ?? "").trim();
  const aboutLinks = (about?.links ?? []).filter(
    (l) => l && typeof l.url === "string" && l.url.trim() && (l.label || "").trim(),
  );
  const aboutHasContent =
    aboutBody.length > 0 || aboutTitle.length > 0 || aboutLinks.length > 0;

  function send() {
    const msg = draft.trim();
    if (!msg) return;
    setDraft("");
    onSend(msg);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  }

  if (!open) {
    const label = (launcherLabel || "").trim();
    return (
      <button
        type="button"
        className={label ? "gw-launcher gw-launcher--labeled" : "gw-launcher"}
        aria-label={label ? `Open chat — ${label}` : "Open support chat"}
        onClick={onOpen}
        onPointerEnter={onLauncherIntent}
        onPointerDown={onLauncherIntent}
        onFocus={onLauncherIntent}
      >
        <span className="gw-launcher-icon" aria-hidden="true">
          <ChatIcon />
        </span>
        {label ? <span className="gw-launcher-label">{label}</span> : null}
      </button>
    );
  }

  return (
    <div
      className={[
        "gw-panel",
        fullscreen ? "gw-panel-fullscreen" : "",
        hostIsMobile ? "gw-mobile" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      role="dialog"
      aria-label="Support chat"
    >
      <header className="gw-header">
        {logoUrl ? (
          <img
            className="gw-header-logo"
            src={logoUrl}
            alt=""
            aria-hidden="true"
          />
        ) : null}
        <div className="gw-header-title-group">
          <div className="gw-header-title-row">
            <div className="gw-header-title">{title}</div>
            {beta ? <span className="gw-beta-badge">Beta</span> : null}
            {aboutHasContent ? (
              <button
                type="button"
                className="gw-about-btn"
                aria-label="About this chatbot"
                title="About this chatbot"
                onClick={() => setAboutOpen(true)}
              >
                <InfoIcon />
              </button>
            ) : null}
          </div>
          {tagline && tagline.trim() ? (
            <div className="gw-header-tagline">{tagline.trim()}</div>
          ) : null}
        </div>
        {authSlot ? <div className="gw-header-auth">{authSlot}</div> : null}
        {appMode || hostIsMobile ? null : (
          <button
            type="button"
            className="gw-icon-btn"
            aria-label={fullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            onClick={onToggleFullscreen}
          >
            {fullscreen ? <CollapseIcon /> : <ExpandIcon />}
          </button>
        )}
        {appMode ? null : (
          <button
            type="button"
            className="gw-icon-btn"
            aria-label="Close chat"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        )}
      </header>

      {aboutOpen && aboutHasContent ? (
        <div
          className="gw-about-overlay"
          role="dialog"
          aria-label={aboutTitle || `About ${title}`}
        >
          <button
            type="button"
            aria-label="Dismiss"
            className="gw-about-backdrop"
            onClick={() => setAboutOpen(false)}
          />
          <div className="gw-about-modal">
            <div className="gw-about-head">
              <span className="gw-about-title">{aboutTitle || title}</span>
              <button
                type="button"
                aria-label="Close"
                className="gw-about-close"
                onClick={() => setAboutOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            {aboutBody ? (
              <div className="gw-about-body">
                {aboutBody.split(/\n{2,}/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            ) : null}
            {aboutLinks.length > 0 ? (
              <ul className="gw-about-links">
                {aboutLinks.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      ) : null}

      {children}

      <footer className="gw-footer">
        <div className="gw-composer">
          <textarea
            className="gw-textarea"
            placeholder="Type a message…"
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={streaming}
          />
          <button
            type="button"
            className="gw-send"
            onClick={send}
            disabled={streaming || draft.trim().length === 0}
          >
            {streaming ? "…" : "Send"}
          </button>
        </div>
      </footer>
    </div>
  );
}

// ---------------- inline icons ----------------

function ChatIcon() {
  // Speech-bubble silhouette with a cross centered inside. The cross
  // uses a thicker stroke than the bubble outline so it reads at the
  // small launcher size. Single-color (inherits ``currentColor`` from
  // the launcher button) so it tints to the tenant's accent gradient.
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
        fill="none"
      />
      <path
        d="M12 8v6.5M9 10.5h6"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 14v6h6M20 10V4h-6M20 4l-7 7M4 20l7-7" />
    </svg>
  );
}

function CollapseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 20v-5H4M20 9h-5V4M15 4v5h5M4 15h5v5" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v5h1" />
    </svg>
  );
}
